<template>
  <div class="app-container">
    <h1>综合学情分析</h1>

    <!-- 学情分析卡片 -->
    <el-card class="analysis-card" v-if="analysisData">
      <div slot="header" class="analysis-header">
        <span><i class="el-icon-data-analysis"></i> 学情分析报告</span>
        <div class="analysis-actions">
          <el-button type="primary" size="small" @click="generateAnalysis" :loading="generating">
            <i class="el-icon-refresh"></i> 重新分析
          </el-button>
          <el-button size="small" @click="getLatestAnalysis" :loading="loading">
            <i class="el-icon-view"></i> 查看最新
          </el-button>
          <el-button size="small" type="info" @click="goToUserbook">
            <i class="el-icon-notebook-1"></i> 查看错题本
          </el-button>
          <el-button size="small" type="warning" @click="compareWithUserbook">
            <i class="el-icon-data-analysis"></i> 数据对比
          </el-button>
        </div>
      </div>

      <div class="analysis-content">
        <!-- 总体统计 -->
        <div class="overview-stats">
          <div class="stat-item">
            <div class="stat-number">{{ analysisData.overallCorrectRate }}%</div>
            <div class="stat-label">总体正确率</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ analysisData.totalQuestions }}</div>
            <div class="stat-label">总题目数</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ analysisData.correctQuestions }}</div>
            <div class="stat-label">正确题目</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ analysisData.wrongQuestions }}</div>
            <div class="stat-label">错误题目</div>
          </div>
        </div>

        <!-- 分数统计 -->
        <div class="score-stats">
          <h3>分数统计</h3>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="score-item">
                <div class="score-label">平均分</div>
                <div class="score-value">{{ analysisData.averageScore ? analysisData.averageScore.toFixed(1) : '0.0' }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="score-item">
                <div class="score-label">最高分</div>
                <div class="score-value">{{ analysisData.highestScore ? analysisData.highestScore.toFixed(1) : '0.0' }}</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="score-item">
                <div class="score-label">最低分</div>
                <div class="score-value">{{ analysisData.lowestScore ? analysisData.lowestScore.toFixed(1) : '0.0' }}</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 科目分析 -->
        <div class="subject-analysis" v-if="analysisData.subjectAnalysis">
          <h3>科目分析</h3>
          <el-table :data="analysisData.subjectAnalysis" border size="small">
            <el-table-column prop="subject" label="科目" width="120" />
            <el-table-column prop="totalQuestions" label="总题目" width="100" align="center" />
            <el-table-column prop="correctQuestions" label="正确" width="100" align="center" />
            <el-table-column prop="wrongQuestions" label="错误" width="100" align="center" />
            <el-table-column prop="correctRate" label="正确率" width="120" align="center">
              <template slot-scope="scope">
                <span :class="getCorrectRateClass(scope.row.correctRate)">
                  {{ scope.row.correctRate }}%
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="averageScore" label="平均分" width="120" align="center">
              <template slot-scope="scope">
                {{ scope.row.averageScore ? scope.row.averageScore.toFixed(1) : '0.0' }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" align="center">
              <template slot-scope="scope">
                <el-button size="mini" type="primary" @click="goToUserbookSubject(scope.row.subject)">
                  查看详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 题目类型分析 -->
        <div class="question-type-analysis" v-if="analysisData.questionTypeAnalysis">
          <h3>题目类型分析</h3>
          <el-table :data="analysisData.questionTypeAnalysis" border size="small">
            <el-table-column prop="questionType" label="题目类型" width="120" />
            <el-table-column prop="totalQuestions" label="总题目" width="100" align="center" />
            <el-table-column prop="correctQuestions" label="正确" width="100" align="center" />
            <el-table-column prop="wrongQuestions" label="错误" width="100" align="center" />
            <el-table-column prop="correctRate" label="正确率" width="120" align="center">
              <template slot-scope="scope">
                <span :class="getCorrectRateClass(scope.row.correctRate)">
                  {{ scope.row.correctRate }}%
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="averageScore" label="平均分" width="120" align="center">
              <template slot-scope="scope">
                {{ scope.row.averageScore ? scope.row.averageScore.toFixed(1) : '0.0' }}
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 薄弱知识点 -->
        <div class="weak-knowledge" v-if="analysisData.weakKnowledge">
          <h3>薄弱知识点</h3>
          <div class="knowledge-tags">
            <el-tag
              v-for="knowledge in analysisData.weakKnowledge"
              :key="knowledge"
              type="danger"
              size="medium"
              style="margin-right: 10px; margin-bottom: 10px; cursor: pointer;"
              @click="goToWeakKnowledge(knowledge)"
            >
              {{ knowledge }}
            </el-tag>
          </div>
          <div class="knowledge-actions">
            <el-button type="primary" size="small" @click="goToCreate">
              <i class="el-icon-edit"></i> 生成个性作业
            </el-button>
          </div>
        </div>

        <!-- 学习建议 -->
        <div class="suggestions" v-if="analysisData.suggestions">
          <h3>学习建议</h3>
          <ul class="suggestion-list">
            <li v-for="(suggestion, index) in analysisData.suggestions" :key="index">
              {{ suggestion }}
            </li>
          </ul>
        </div>
      </div>
    </el-card>

    <!-- 无数据时的默认显示 -->
    <el-card v-else class="empty-analysis-card">
      <div slot="header" class="analysis-header">
        <span><i class="el-icon-data-analysis"></i> 学情分析报告</span>
      </div>
      <div class="empty-content">
        <div class="empty-icon">
          <i class="el-icon-data-analysis"></i>
        </div>
        <div class="empty-text">
          <h3>暂无学情分析数据</h3>
          <p>点击下方按钮生成您的学情分析报告</p>
        </div>
        <div class="empty-actions">
          <el-button type="primary" size="large" @click="generateAnalysis" :loading="generating">
            <i class="el-icon-refresh"></i> 生成学情分析
          </el-button>
          <el-button size="large" @click="getLatestAnalysis" :loading="loading">
            <i class="el-icon-view"></i> 查看最新分析
          </el-button>
        </div>
      </div>
    </el-card>

    <!-- 数据对比弹窗 -->
    <el-dialog title="学情分析与错题本数据对比" :visible.sync="showDataComparison" width="800px">
      <div v-if="userbookStats && analysisData" class="comparison-content">
        <el-row :gutter="20">
          <el-col :span="12">
            <h4>学情分析数据</h4>
            <div class="comparison-item">
              <span>总错题数：</span>
              <span class="comparison-value">{{ analysisData.wrongQuestions }}</span>
            </div>
            <div class="comparison-item">
              <span>总题目数：</span>
              <span class="comparison-value">{{ analysisData.totalQuestions }}</span>
            </div>
            <div class="comparison-item">
              <span>正确率：</span>
              <span class="comparison-value">{{ analysisData.overallCorrectRate }}%</span>
            </div>
          </el-col>
          <el-col :span="12">
            <h4>错题本数据</h4>
            <div class="comparison-item">
              <span>总错题数：</span>
              <span class="comparison-value">{{ userbookStats.totalWrongQuestions }}</span>
            </div>
            <div class="comparison-item">
              <span>科目分布：</span>
              <div class="subject-distribution">
                <div v-for="(count, subject) in userbookStats.subjectStats" :key="subject" class="subject-item">
                  {{ subject }}: {{ count }}
                </div>
              </div>
            </div>
          </el-col>
        </el-row>

        <div class="comparison-actions">
          <el-button type="primary" @click="syncData" :loading="generating">
            同步数据
          </el-button>
          <el-button @click="showDataComparison = false">关闭</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 错误提示 -->
    <el-alert
      v-if="errorMessage"
      :title="errorMessage"
      type="warning"
      show-icon
      :closable="false"
      style="margin-top: 20px;"
    >
      <template slot="default">
        <p>{{ errorMessage }}</p>
        <el-button type="primary" size="small" @click="generateAnalysis" :loading="generating">
          重新生成分析
        </el-button>
      </template>
    </el-alert>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { generateMyAnalysis, getMyLatestAnalysis } from '@/api/studentAnalysis'
import { getStudentErrorStats } from '@/api/errorAnswerAnalysis'

export default {
  name: 'weakknowledge',
  data() {
    return {
      checkList: [],
      analysisData: null,
      loading: false,
      generating: false,
      errorMessage: '',
      userbookStats: null,
      showDataComparison: false
    }
  },
  computed: {
    ...mapGetters([
      'roles'
    ])
  },
  created() {
    // 检查用户角色
    if (!this.roles.includes('student')) {
      this.$message.error('您没有权限访问此页面')
      this.$router.push('/404')
    } else {
      // 获取学情分析数据
      this.getLatestAnalysis()
    }
  },
  methods: {
    goTocreate() {
      this.$router.push({ name: 'createComponent' })
    },

    goToUserbook() {
      this.$router.push({ name: 'wrong-book' })
    },

    goToUserbookSubject(subject) {
      this.$router.push({
        name: 'UserbookSubjectDetail',
        params: { subject: subject }
      })
    },

    goToWeakKnowledge(knowledge) {
      this.$router.push({
        path: '/weakknowledge/create',
        query: { knowledge: knowledge, autoAnalyze: 'true' }
      })
    },

    async compareWithUserbook() {
      try {
        const token = this.$store.getters.token
        const userInfo = JSON.parse(JSON.parse(atob(token.split('.')[1])).userInfo)
        const userId = userInfo.id

        const res = await getStudentErrorStats(userId)
        if (res && res.data) {
          this.userbookStats = res.data
          this.showDataComparison = true
        }
      } catch (error) {
        console.error('获取错题本统计失败:', error)
        this.$message.error('获取错题本统计失败')
      }
    },

    async syncData() {
      this.generating = true
      try {
        await this.generateAnalysis()
        this.$message.success('数据同步成功')
        this.showDataComparison = false
      } catch (error) {
        this.$message.error('数据同步失败')
      } finally {
        this.generating = false
      }
    },

    // 获取最新学情分析
    async getLatestAnalysis() {
      this.loading = true
      this.errorMessage = ''
      try {
        const res = await getMyLatestAnalysis()
        if (res && res.data) {
          this.analysisData = res.data
        } else {
          this.analysisData = null
        }
      } catch (error) {
        console.error('获取学情分析失败:', error)
        this.errorMessage = '获取学情分析数据失败，请重试'
        this.analysisData = null
      } finally {
        this.loading = false
      }
    },

    // 生成学情分析
    async generateAnalysis() {
      this.generating = true
      this.errorMessage = ''
      try {
        const res = await generateMyAnalysis()
        if (res && res.data) {
          this.analysisData = res.data
          this.$message.success('学情分析生成成功')
        } else {
          this.$message.error('生成学情分析失败')
        }
      } catch (error) {
        console.error('生成学情分析失败:', error)
        this.errorMessage = '生成学情分析失败，请重试'
        this.$message.error('生成学情分析失败，请重试')
      } finally {
        this.generating = false
      }
    },

    // 根据正确率返回样式类
    getCorrectRateClass(rate) {
      if (rate >= 80) return 'correct-rate-high'
      if (rate >= 60) return 'correct-rate-medium'
      return 'correct-rate-low'
    }
  }
}
</script>

<style scoped>
.analysis-card {
  margin-bottom: 30px;
}

.empty-analysis-card {
  margin-bottom: 30px;
  text-align: center;
}

.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.analysis-header span {
  font-size: 18px;
  font-weight: bold;
  color: #409EFF;
}

.analysis-actions {
  display: flex;
  gap: 10px;
}

.analysis-content {
  padding: 20px 0;
}

.empty-content {
  padding: 40px 20px;
}

.empty-icon {
  font-size: 80px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-text h3 {
  color: #606266;
  margin-bottom: 10px;
}

.empty-text p {
  color: #909399;
  margin-bottom: 30px;
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.overview-stats {
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.score-stats {
  margin-bottom: 30px;
}

.score-stats h3 {
  margin-bottom: 15px;
  color: #333;
}

.score-item {
  text-align: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
}

.score-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.score-value {
  font-size: 20px;
  font-weight: bold;
  color: #409EFF;
}

.subject-analysis,
.question-type-analysis,
.weak-knowledge,
.suggestions {
  margin-bottom: 30px;
}

.subject-analysis h3,
.question-type-analysis h3,
.weak-knowledge h3,
.suggestions h3 {
  margin-bottom: 15px;
  color: #333;
}

.suggestion-list {
  padding-left: 20px;
}

.suggestion-list li {
  margin-bottom: 8px;
  color: #666;
  line-height: 1.6;
}

.correct-rate-high {
  color: #67C23A;
  font-weight: bold;
}

.correct-rate-medium {
  color: #E6A23C;
  font-weight: bold;
}

.correct-rate-low {
  color: #F56C6C;
  font-weight: bold;
}

.knowledge-tags {
  margin-bottom: 15px;
}

.knowledge-actions {
  margin-top: 10px;
}

.comparison-content {
  padding: 20px 0;
}

.comparison-item {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.comparison-value {
  font-weight: bold;
  color: #409EFF;
}

.subject-distribution {
  margin-top: 10px;
}

.subject-item {
  margin-bottom: 5px;
  padding: 5px 10px;
  background: #f5f7fa;
  border-radius: 4px;
}

.comparison-actions {
  margin-top: 20px;
  text-align: center;
}
</style>
